import { DetailBox, Icon } from '@octano/global-ui';
import { Card, Col, Row, CardBody, FormText } from 'reactstrap';
import GoBackToListButton from '../../../../../components/buttons/GoBackToListButton';
import { useDebtDetailLoader } from './DebtDetailLoader';
import Loading from '../../../../../components/info/Loading';
import Tooltip from '../../../../../components/tooltip/Tooltip';
import { useTranslation } from 'react-i18next';

const formatter = (value: number) =>
  `$${value.toLocaleString('es-CL', {
    style: 'decimal',
    currency: 'CLP',
  })}`;

const dictPrefix = 'financialPortal.currentAccount.tabs.currentAccount.detail';

// Rows ordered by imputation priority (first-drained on top) — see
// DebtImputationService in the API.
const CONCEPT_BY_LABEL_KEY = {
  collectionFees: 'collectionFees',
  lateInterestAmount: 'lateInterest',
  interestAmount: 'currentInterest',
  capitalAmount: 'capital',
} as const;

function DebtDetailCard() {
  const { data, loading } = useDebtDetailLoader();
  const { t } = useTranslation();

  if (loading || !data) {
    return (
      <div className="d-flex justify-content-center">
        <Loading />
      </div>
    );
  }

  const {
    studentName,
    rutOrPassport,
    cosignerName,
    concept,
    numberOfInstallment,
    expirationDate,
    daysLate,
    interestRate,
    capitalAmount,
    interestAmount,
    lateInterestAmount,
    collectionFees,
    paidBreakdown,
    outstandingBreakdown,
    totalAmount,
    paidAmount,
    outstandingDebt,
    amountGross,
    amountDiscounts,
    discounts,
  } = data;

  const CardItem = ({
    title,
    content,
    paddingTop = '13px',
    paddingRight = '20px',
    contentSize = '16px',
    contentColor = '',
    tooltip = '',
    minContentWidth = '80px',
  }: {
    title: string;
    content: string;
    paddingTop?: string;
    paddingRight?: string;
    contentSize?: string;
    contentColor?: string;
    tooltip?: string;
    minContentWidth?: string;
  }) => {
    return (
      <div className="d-flex flex-column" style={{ paddingTop, paddingRight }}>
        <p className="text-muted m-0" style={{ fontSize: '14px' }}>
          {title}
        </p>
        <FormText
          className="font-weight-bold m-0"
          style={{
            fontSize: contentSize,
            display: 'flex',
            gap: '10px',
            maxWidth: '250px',
            minWidth: minContentWidth,
          }}
          color={contentColor}
        >
          {content}
          {tooltip && (
            <Tooltip id={`info_tooltip`} placement="right" text={tooltip}>
              <Icon name="information" size={14.71} />
            </Tooltip>
          )}
        </FormText>
      </div>
    );
  };

  return (
    <Card className="p-4 mx-3">
      <Row>
        <Col className="mb-4" xs={12}>
          <div className="d-flex flex-wrap w-100 align-items-start justify-content-center">
            <GoBackToListButton />
            <div className="flex-fill px-2" />
            <Col>
              <DetailBox
                columns={[
                  { title: t(`${dictPrefix}.studentName`), body: studentName },
                  { title: t(`${dictPrefix}.studentId`), body: rutOrPassport },
                ]}
              />
            </Col>
          </div>
        </Col>
        <Col>
          <Card className="border border-primary p-2" style={{ width: '80%' }}>
            <div className="px-3 pt-3">
              <p className="fs-20 font-weight-bold m-0">
                {t(`${dictPrefix}.cardTitle`)}
              </p>
            </div>
            <CardBody>
              <div className="d-flex flex-row flex-wrap gap-4">
                <CardItem
                  title={t(`${dictPrefix}.outstandingDebt`)}
                  content={formatter(outstandingDebt)}
                  paddingRight="50px"
                  contentSize="30px"
                  paddingTop="0"
                  contentColor="primary"
                  minContentWidth="120px"
                />
                <CardItem
                  title={t(`${dictPrefix}.concept`)}
                  content={concept}
                  contentSize={'14px'}
                  minContentWidth="250px"
                />
                <CardItem
                  title={t(`${dictPrefix}.installmentNumber`)}
                  content={numberOfInstallment}
                />
                <CardItem
                  title={t(`${dictPrefix}.capitalAmount`)}
                  content={formatter(capitalAmount)}
                />
                <CardItem
                  title={t(`${dictPrefix}.totalAmount`)}
                  content={formatter(totalAmount)}
                />
                <CardItem
                  title={t(`${dictPrefix}.interestAmount`)}
                  content={formatter(interestAmount)}
                />
                <CardItem
                  title={t(`${dictPrefix}.lateInterestAmount`)}
                  content={formatter(lateInterestAmount)}
                />

                <div className="w-100 pb-4"></div>

                <CardItem
                  title={t(`${dictPrefix}.paidAmount`)}
                  content={formatter(paidAmount)}
                  paddingRight="50px"
                  contentSize="30px"
                  paddingTop="0"
                  contentColor="primary"
                  minContentWidth="120px"
                />
                <CardItem
                  title={t(`${dictPrefix}.interestRate`)}
                  content={interestRate + '%'}
                  minContentWidth="250px"
                />
                <CardItem
                  title={t(`${dictPrefix}.expirationDate`)}
                  content={expirationDate.replaceAll('-', '/')}
                />
                <CardItem
                  title={t(`${dictPrefix}.daysLate`)}
                  content={`${daysLate} ${t(`${dictPrefix}.daysSuffix`)}`}
                />
                <CardItem
                  title={t(`${dictPrefix}.cosigner`)}
                  content={cosignerName}
                  tooltip={t(`${dictPrefix}.cosignerTooltip`)}
                />
              </div>

              <div className="pt-4">
                <p className="fs-16 font-weight-bold m-0">
                  {t(`${dictPrefix}.conceptBreakdownTitle`)}
                </p>
                <table className="w-100 mt-2" style={{ fontSize: '14px' }}>
                  <thead>
                    <tr className="text-muted">
                      <th className="font-weight-normal" />
                      <th className="font-weight-normal text-right">
                        {t(`${dictPrefix}.conceptTotal`)}
                      </th>
                      <th className="font-weight-normal text-right">
                        {t(`${dictPrefix}.conceptPaid`)}
                      </th>
                      <th className="font-weight-normal text-right">
                        {t(`${dictPrefix}.conceptOutstanding`)}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      [
                        ['collectionFees', collectionFees],
                        ['lateInterestAmount', lateInterestAmount],
                        ['interestAmount', interestAmount],
                        ['capitalAmount', capitalAmount],
                      ] as const
                    ).map(([key, total]) => {
                      const concept = CONCEPT_BY_LABEL_KEY[key];
                      return (
                        <tr key={key}>
                          <td className="text-muted">{t(`${dictPrefix}.${key}`)}</td>
                          <td className="text-right">{formatter(total)}</td>
                          <td className="text-right">
                            {formatter(paidBreakdown[concept])}
                          </td>
                          <td className="text-right font-weight-bold">
                            {formatter(outstandingBreakdown[concept])}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {discounts.length > 0 && (
                <div className="pt-4">
                  <p className="fs-16 font-weight-bold m-0">
                    {t(`${dictPrefix}.discountsTitle`)}
                  </p>
                  <div className="d-flex justify-content-between pt-2">
                    <span className="text-muted">
                      {t(`${dictPrefix}.discountsGross`)}
                    </span>
                    <span>{formatter(amountGross)}</span>
                  </div>
                  {discounts.map((discount) => (
                    <div
                      className="d-flex justify-content-between"
                      key={discount.code}
                    >
                      <span className="text-muted">
                        {discount.name}
                        {discount.percentage !== null
                          ? ` (${discount.percentage}%)`
                          : ''}
                      </span>
                      <span>-{formatter(discount.amount)}</span>
                    </div>
                  ))}
                  <div className="d-flex justify-content-between font-weight-bold pt-1">
                    <span>{t(`${dictPrefix}.discountsNet`)}</span>
                    <span>{formatter(amountGross - amountDiscounts)}</span>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Card>
  );
}

export default DebtDetailCard;
